import type { TipTapDoc } from "@compass/shared/schema/tiptapDoc";
import { getPlainTextFromDoc } from "@compass/shared/tiptap/plaintext";
import {
    DocumentPlusIcon,
    MinusCircleIcon,
    PencilIcon,
    PlusCircleIcon,
} from "@heroicons/react/24/solid";
import * as Dialog from "@radix-ui/react-dialog";
import { type Editor } from "@tiptap/react";
import { Dispatch, ReactNode, SetStateAction, useCallback, useEffect, useState } from "react";
import { ReactComponent as CloseIcon } from "assets/img/icons/header/close.svg";
import { ReactComponent as SubmitIcon } from "assets/img/icons/submit.svg";
import { Avatar, Button, FileAttachment, Input, SwitchInput } from "components/elements";
import { ManageAttachmentsModal } from "components/elements/ManageAttachmentsModal";
import { PostEditor } from "components/form/PostEditor";
import { UploadDocument } from "components/form/UploadDocument";
import {
    ClientCurrentUser,
    ClientDocument,
    ClientPost,
    PostCreate,
    PostUpdate,
} from "@compass/shared/types";

type SocialPostEditorProps = {
    handleEditPost: (params: any, callback: () => void) => void;
    isLoading?: boolean;
    isEdit?: boolean;
    isOpen?: number | boolean | "new-post";
    post?: ClientPost;
    setIsOpen: Dispatch<SetStateAction<any>>;
    children?: ReactNode;
    currentUser?: ClientCurrentUser;
};

const PollForm = ({
    pollOptions,
    setPollOptions,
}: {
    pollOptions: string[];
    setPollOptions: Dispatch<SetStateAction<string[] | undefined>>;
}) => {
    return (
        <div className="mt-5 flex w-full flex-col gap-2">
            <div className="text-sm font-medium text-slate-600">Edit Poll Choices</div>
            <div className="flex w-full flex-col items-center gap-1">
                {pollOptions.map((vote: string, key: number) => {
                    return (
                        <div className="flex w-full items-center gap-2 rounded-md bg-slate-100 ">
                            <input
                                value={vote}
                                onChange={(e) =>
                                    setPollOptions([
                                        ...pollOptions.map((v, k) =>
                                            key === k ? e.target.value : v,
                                        ),
                                    ])
                                }
                                className="w-full bg-transparent px-2 py-1 text-sm text-slate-900 placeholder:font-medium placeholder:text-slate-500 focus:outline-none"
                                type="text"
                                placeholder="Write your option here..."
                            />
                            <Button
                                variant="hidden"
                                size="smallest"
                                className={`flex gap-2 whitespace-nowrap text-slate-500 ${
                                    pollOptions.length <= 2
                                        ? "cursor-not-allowed opacity-50"
                                        : "cursor-pointer"
                                }`}
                                onClick={() =>
                                    setPollOptions([...pollOptions.filter((_, k) => k !== key)])
                                }
                                disabled={pollOptions.length <= 2}
                            >
                                <MinusCircleIcon className="h-4 w-4" />
                            </Button>
                        </div>
                    );
                })}
            </div>
            <div className="mt-2 flex gap-2">
                <Button
                    variant="secondary"
                    size="smallest"
                    className="flex gap-2 whitespace-nowrap"
                    onClick={() => setPollOptions([...pollOptions, ""])}
                >
                    <PlusCircleIcon className="h-4 w-4" /> Add Option
                </Button>
                <Button
                    variant="secondary"
                    size="smallest"
                    className="flex gap-2 whitespace-nowrap border-red-300 text-red-600 hover:bg-red-50"
                    onClick={() => setPollOptions(undefined)}
                >
                    Remove Poll
                </Button>
            </div>
        </div>
    );
};

const AttachmentsForm = ({
    setAttachments,
    attachments,
    clearAttachments,
}: {
    attachments: ClientDocument[];
    setAttachments: Dispatch<SetStateAction<ClientDocument[]>>;
    clearAttachments: () => void;
}) => {
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showManageModal, setShowManageModal] = useState(false);

    const addDocument = (doc: ClientDocument) => {
        setAttachments((prev) => (prev.some((a) => a.id === doc.id) ? prev : [...prev, doc]));
    };

    const addDocuments = (docs: ClientDocument[]) => {
        setAttachments((prev) => {
            const existingIds = new Set(prev.map((a) => a.id));
            const newDocs = docs.filter((d) => !existingIds.has(d.id));
            return [...prev, ...newDocs];
        });
        setShowManageModal(false);
    };

    return (
        <div className="flex flex-1 flex-col gap-2">
            {attachments.length > 0 && (
                <div className="flex max-h-48 flex-col gap-2 overflow-y-auto">
                    {attachments.map((attachment) => (
                        <FileAttachment
                            key={attachment.id}
                            file={attachment}
                            onRemove={() =>
                                setAttachments(attachments.filter((a) => a.id !== attachment.id))
                            }
                            showAIDetails={false}
                        />
                    ))}
                </div>
            )}
            <div className="flex w-full flex-wrap gap-2">
                <Button
                    type="button"
                    variant="secondary"
                    size="smallest"
                    className="flex gap-2 whitespace-nowrap"
                    onClick={() => setShowUploadModal(true)}
                >
                    New attachment
                </Button>
                <Button
                    type="button"
                    variant="secondary"
                    size="smallest"
                    className="flex gap-2 whitespace-nowrap"
                    onClick={() => setShowManageModal(true)}
                >
                    Previous attachments
                </Button>
                {attachments.length > 0 && (
                    <Button
                        variant="secondary"
                        size="smallest"
                        className="ml-auto flex gap-2 whitespace-nowrap"
                        onClick={clearAttachments}
                    >
                        {attachments.length > 1 ? "Remove all" : "Remove file"}
                    </Button>
                )}
            </div>
            <UploadDocument
                isOpen={showUploadModal}
                setIsOpen={setShowUploadModal}
                onUpload={addDocument}
                button={null}
                title="Upload Document"
                blur={false}
                defaultPublic
                notifyContext="social"
            />
            <ManageAttachmentsModal
                isOpen={showManageModal}
                setIsOpen={setShowManageModal}
                onAttach={addDocuments}
                alreadyAttachedIds={new Set(attachments.map((a) => a.id))}
                button={null}
                blur={false}
                showPublicFiles
            />
        </div>
    );
};

const PostTitle = ({
    setTitle,
    title,
    isEdit,
}: {
    setTitle: Dispatch<SetStateAction<string>>;
    title: string;
    isEdit?: boolean;
}) => {
    return (
        <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={120}
            className={`w-full rounded-md bg-[#f7f7f7] px-2 py-2 text-sm font-medium leading-5 placeholder:text-base placeholder:font-normal placeholder:text-gray-400 focus:outline-none ${
                isEdit ? "bg-slate-50" : ""
            }`}
            type="text"
            placeholder="Title your post"
        />
    );
};

const SectionButton = ({ children, onClick }: { children: ReactNode; onClick: () => void }) => {
    return (
        <div
            className="flex w-full flex-1 cursor-pointer justify-center rounded-md border-2 border-dashed border-slate-300 py-4 text-sm font-medium text-slate-600"
            onClick={onClick}
        >
            {children}
        </div>
    );
};

export const SocialPostEditor = ({
    handleEditPost,
    isLoading,
    isOpen,
    setIsOpen,
    isEdit,
    post,
    children,
    currentUser,
}: SocialPostEditorProps) => {
    const [editor, setEditor] = useState<Editor | null>(null);
    const editorRef = useCallback((editorInstance: Editor | null) => {
        if (editorInstance) {
            setEditor(editorInstance);
        }
    }, []);
    const [isAnonymous, setAnonymous] = useState<boolean>(false);
    const [pollOptions, setPollOptions] = useState<string[] | undefined>();
    const [title, setTitle] = useState<string>("");
    const [editorDoc, setEditorDoc] = useState<TipTapDoc | null>(null);
    const editorPlainText = editorDoc ? getPlainTextFromDoc(editorDoc) : "";
    const [attachments, setAttachments] = useState<ClientDocument[]>([]);
    const [showAttachmentsForm, setShowAttachmentsForm] = useState<boolean>(false);

    const MAX_LENGTH = 3000;

    const clearAttachments = () => {
        setAttachments([]);
        setShowAttachmentsForm(false);
    };

    useEffect(() => {
        if (!isEdit) {
            setEditorDoc(null);
            setTitle("");
            return;
        }
        if (!editor || !post) return;

        const existingAttachments: ClientDocument[] = (post.images || []).map((img) => ({
            id: img.id,
            uploaded: img.uploaded,
            name: img.name,
            type: img.type,
        }));
        setAttachments(existingAttachments);
        if (existingAttachments.length > 0 && isEdit) {
            setShowAttachmentsForm(true);
        }
        setTitle(post.title || "");

        if (post.contentJson) {
            editor?.commands.setContent(post.contentJson as object);
            setEditorDoc(post.contentJson);
        }
        editor?.commands.focus();

        const pollOptionsData =
            post?.poll && post?.poll?.pollOptions?.length > 0
                ? post?.poll?.pollOptions.map((item) => item.content)
                : [];

        setPollOptions(pollOptionsData);
    }, [post, editor, isEdit]);

    const handleEdit = () => {
        let params: PostCreate | PostUpdate;
        const attachmentIds = attachments.map((a) => a.id);

        const filteredPollOptions = pollOptions?.filter((o) => o.trim().length > 0);

        // For edits: undefined pollOptions means the user removed the poll → send [] so the
        // server deletes existing options. An empty array means no poll was ever shown → omit.
        const editPollPayload = isEdit
            ? pollOptions === undefined
                ? []
                : (filteredPollOptions?.length ?? 0) > 0
                  ? filteredPollOptions
                  : undefined
            : filteredPollOptions;

        if (!editorDoc) return;

        if (isEdit) {
            params = {
                contentJson: editorDoc,
                attachmentIds,
                title,
                postId: post?.id,
                pollOptions: editPollPayload,
            } as PostUpdate;
        } else {
            params = {
                contentJson: editorDoc,
                attachmentIds,
                title,
                isAnonymous,
                pollOptions: editPollPayload,
            };
        }

        handleEditPost(params, clearPostEditor);
    };

    const clearPostEditor = () => {
        setAttachments([]);
        setAnonymous(false);
        setTitle("");
        setPollOptions(undefined);
        editor?.commands.setContent("");
        setIsOpen(false);
    };

    const getPollError = (pollOptions: string[] | undefined): string | null => {
        if (!pollOptions || pollOptions.length === 0) return null;
        if (pollOptions.some((option) => option.trim().length === 0))
            return "All poll options must be filled in";
        if (pollOptions.length < 2) return "At least 2 poll options are required";
        const uniqueOptions = new Set(pollOptions.map((o) => o.trim().toLowerCase()));
        if (uniqueOptions.size !== pollOptions.length) return "Poll options must be unique";
        return null;
    };

    const isPollValid = (pollOptions: string[] | undefined): boolean =>
        getPollError(pollOptions) === null;

    const isOverLimit = editorPlainText.length > MAX_LENGTH;

    const hasContent = editorPlainText.trim().length >= 1;
    const hasImages = attachments.length > 0;
    const hasPoll = (pollOptions?.length ?? 0) > 0;
    const hasAtLeastOneContentType = hasContent || hasImages || hasPoll;

    const originalPollOptions = post?.poll?.pollOptions?.map((item) => item.content) ?? [];
    const pollOptionsChanged =
        JSON.stringify(pollOptions ?? []) !== JSON.stringify(originalPollOptions);

    const originalAttachmentIds = [...(post?.images ?? []).map((i) => i.id)].sort();
    const currentAttachmentIds = [...attachments.map((a) => a.id)].sort();
    const attachmentsChanged =
        JSON.stringify(currentAttachmentIds) !== JSON.stringify(originalAttachmentIds);

    const isPostValid = isEdit
        ? (title.trim() !== (post?.title ?? "").trim() ||
              JSON.stringify(editorDoc) !== JSON.stringify(post?.contentJson ?? null) ||
              pollOptionsChanged ||
              attachmentsChanged) &&
          title &&
          hasAtLeastOneContentType &&
          isPollValid(pollOptions)
        : title && hasAtLeastOneContentType && isPollValid(pollOptions) && !isOverLimit;

    return (
        <Dialog.Root
            open={Boolean(isOpen)}
            onOpenChange={(opened) => {
                if (!opened) {
                    clearPostEditor();
                } else {
                    setIsOpen(opened);
                }
            }}
        >
            <Dialog.Trigger asChild>{children ?? null}</Dialog.Trigger>

            <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 z-50 bg-[#424242]/75" />
                <Dialog.Content
                    onOpenAutoFocus={(e) => e.preventDefault()}
                    className="fixed left-1/2 top-1/2 z-50 w-[500px] -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-4 shadow-none"
                >
                    <Dialog.Close asChild>
                        <button className="absolute right-4 top-4" aria-label="Close">
                            <CloseIcon className="h-[30px] w-[30px]" />
                        </button>
                    </Dialog.Close>
                    <div className="max-h-[100vh] overflow-auto px-2">
                        <div className="h-2" />

                        <div className="flex items-center justify-start gap-3">
                            <div className="flex items-center gap-2">
                                <Avatar
                                    className="h-[50px] w-[50px] shrink-0 bg-slate-200 text-xs text-slate-400"
                                    photoUrl={
                                        isEdit
                                            ? post &&
                                              (post.author.type === "identified" ||
                                                  post.author.type === "anonymous-self")
                                                ? post.author.avatarUrl
                                                : null
                                            : isAnonymous
                                              ? null
                                              : currentUser?.avatarUrl
                                    }
                                    name={
                                        isEdit
                                            ? currentUser?.name || "AN"
                                            : isAnonymous
                                              ? "AN"
                                              : currentUser?.name
                                    }
                                />
                                <div className="text-sm font-medium text-slate-900">
                                    {isEdit
                                        ? currentUser?.name
                                        : isAnonymous
                                          ? "Anonymous"
                                          : currentUser?.name}
                                </div>
                            </div>

                            <div className="mt-0.5">
                                {!isEdit && (
                                    <SwitchInput
                                        name="anonymous"
                                        value={isAnonymous}
                                        label="Post Anonymously"
                                        onChange={setAnonymous}
                                    />
                                )}
                            </div>
                        </div>

                        <div className="h-4" />

                        <div className="flex flex-col gap-3">
                            <div>
                                <PostTitle setTitle={setTitle} title={title} />
                                <p className="m-0 text-end text-sm text-gray-600">
                                    {title.length}/120
                                </p>
                            </div>
                            <PostEditor
                                ref={editorRef}
                                wrapperClassName="rounded-md bg-[#eeeeee]"
                                onChange={(doc) => setEditorDoc(doc)}
                                placeholder="Share something with the community..."
                                componentType="socialpost"
                            />

                            {isOverLimit && (
                                <p className="text-end text-sm text-red-600">
                                    You have exceeded the {MAX_LENGTH} character limit.
                                    <span className="text-[#424242]">
                                        ({editorPlainText.length})
                                    </span>
                                </p>
                            )}

                            <div className={pollOptions?.length ? "" : "flex gap-2"}>
                                {showAttachmentsForm ? (
                                    <AttachmentsForm
                                        attachments={attachments}
                                        setAttachments={setAttachments}
                                        clearAttachments={clearAttachments}
                                    />
                                ) : (
                                    <SectionButton onClick={() => setShowAttachmentsForm(true)}>
                                        <DocumentPlusIcon className="h-4 w-4" /> Attach File
                                    </SectionButton>
                                )}

                                {pollOptions?.length ? (
                                    (() => {
                                        const pollError = getPollError(pollOptions);
                                        return (
                                            <>
                                                <PollForm
                                                    setPollOptions={setPollOptions}
                                                    pollOptions={pollOptions}
                                                />
                                                {pollError && (
                                                    <p className="mt-1 text-xs text-red-500">
                                                        {pollError}
                                                    </p>
                                                )}
                                            </>
                                        );
                                    })()
                                ) : (
                                    <SectionButton onClick={() => setPollOptions(["", ""])}>
                                        Include Poll
                                    </SectionButton>
                                )}
                            </div>

                            <div className="flex w-auto gap-2">
                                <Button
                                    variant="primary"
                                    className="ml-auto flex gap-2 px-5"
                                    isLoading={isLoading}
                                    disabled={!isPostValid}
                                    onClick={!isPostValid ? () => null : handleEdit}
                                >
                                    {isEdit ? (
                                        <>
                                            Update Post <PencilIcon className="h-4 w-4" />
                                        </>
                                    ) : (
                                        <>
                                            Share <SubmitIcon className="ml-2" />
                                        </>
                                    )}
                                </Button>
                                {isEdit ? (
                                    <Button
                                        variant="secondary"
                                        disabled={isLoading}
                                        onClick={clearPostEditor}
                                    >
                                        Cancel
                                    </Button>
                                ) : null}
                            </div>
                        </div>
                    </div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};
